import { useAtomValue, useSetAtom } from 'jotai';

import {
  useAddEngagementCommentMutation,
  useDeleteEngagementCommentMutation,
  useUpdateEngagementCommentMutation,
} from '@/gql/operations';
import { BaseModal, ConfirmationDialog } from '@/shared/components/Modal';
import {
  closeCommentModalAtom,
  commentModalDataAtom,
  commentModalModeAtom,
  isCommentModalOpenAtom,
} from '@/shared/state/atoms';

import { CommentForm } from '../CommentForm';
import type { CommentFormData } from '../CommentForm/types';

interface CommentModalProps {
  engagementRecordId: string;
  currentUserMpId: string;
}

/**
 * Comment modal wrapper that handles add/edit/delete actions for comments.
 * Connects BaseModal with CommentForm/ConfirmationDialog and global modal state.
 */
const CommentModal = ({ engagementRecordId, currentUserMpId }: CommentModalProps) => {
  const isOpen = useAtomValue(isCommentModalOpenAtom);
  const mode = useAtomValue(commentModalModeAtom);
  const data = useAtomValue(commentModalDataAtom) as CommentFormData | null;
  const closeModal = useSetAtom(closeCommentModalAtom);

  const refetchQueries = ['GetFilteredClientRecords'];

  const [addComment] = useAddEngagementCommentMutation({ refetchQueries });
  const [updateComment] = useUpdateEngagementCommentMutation({ refetchQueries });
  const [deleteComment] = useDeleteEngagementCommentMutation({ refetchQueries });

  if (!mode) {
    return null;
  }

  const handleSave = async (formData: CommentFormData) => {
    if (!currentUserMpId) {
      console.error('Unable to save comment: current user MP number could not be identified.');
      return;
    }

    if (mode === 'add') {
      await addComment({
        variables: {
          engagement_records_id: engagementRecordId,
          comments: formData.text ?? '',
          created_by: currentUserMpId,
          updated_by: currentUserMpId,
        },
      });
    } else if (mode === 'edit' && formData.id) {
      await updateComment({
        variables: {
          id: formData.id,
          comments: formData.text ?? '',
          updated_by: currentUserMpId,
        },
      });
    }
    closeModal();
  };

  const handleDelete = async () => {
    if (data?.id) {
      await deleteComment({
        variables: { id: data.id },
      });
    }
    closeModal();
  };

  // Render delete confirmation dialog
  if (mode === 'delete') {
    return (
      <BaseModal open={isOpen} onClose={() => closeModal()} ariaLabelledBy="delete-comment-title">
        <ConfirmationDialog
          title="Delete Comment"
          message="Are you sure you want to delete this comment? This action cannot be undone."
          confirmLabel="Delete"
          cancelLabel="Cancel"
          variant="error"
          onConfirm={() => void handleDelete()}
          onCancel={() => closeModal()}
        />
      </BaseModal>
    );
  }

  // Render add/edit form
  if (mode === 'add' || mode === 'edit') {
    return (
      <BaseModal open={isOpen} onClose={() => closeModal()} ariaLabelledBy="comment-modal-title">
        <CommentForm
          data={data}
          mode={mode}
          onCancel={() => closeModal()}
          onSave={(formData) => void handleSave(formData)}
        />
      </BaseModal>
    );
  }

  return null;
};

export default CommentModal;
